import { MapBrowserEvent, Map as OLMap } from 'ol/index';
import {
  Fill,
  Style,
  Text,
  Icon,
  Stroke
} from 'ol/style';
import { Point } from 'ol/geom';
import { getTagTexturePath } from "../../textures/tag-icons";
import { clearAerialTooltip, setAerialTooltip } from "../../viewer";
import { TagFeature } from "../aerial";
import { TagFile, TagsHandler } from ".";
import { Cluster, Vector as VectorSource } from 'ol/source';
import { Vector as VectorLayer } from 'ol/layer';
import { t } from '../../../localization';

const tagIconSize = 24;

export class AerialTags {
  private tagsHandler: TagsHandler;
  private mapSource: VectorSource<any>;
  private mapLayer: VectorLayer<any>;
  private styleCache = {};
  public hovering = false;

  constructor(tagsHandler: TagsHandler) {
    this.tagsHandler = tagsHandler;
    this.mapSource = new VectorSource();

    this.mapLayer = new VectorLayer({
      zIndex: 2,
      source: new Cluster({
        source: this.mapSource,
      }),
      style: feature => {
        return this.getClusterStyle(feature);
      },
    });

    this.map.addLayer(this.mapLayer);
  }

  get map(): OLMap {
    const viewer = this.tagsHandler.viewer;
    return viewer.minimap.map;
  }

  get numberOfTags() {
    return this.mapSource.getFeatures().length;
  }

  public onMouseMove(event: MapBrowserEvent<UIEvent>) {
    if (this.numberOfTags === 0) return;

    const features = this.getFeatureForEvent(event);
    this.hovering = features.length > 0;

    if (features.length === 0) {
      clearAerialTooltip();
    } else if (features.length === 1) {
      const selected = features[0];
      let tooltipText = this.tagsHandler
        .getTooltipText(selected.tagID);

      setAerialTooltip(tooltipText);
    } else {
      setAerialTooltip([
        t("tags.multiple-tags", {
          count: features.length
        })
      ]);
    }
  }

  getFeatureForEvent(event: MapBrowserEvent<UIEvent>): TagFeature[] {
    const clusters = this.map.getFeaturesAtPixel(event.pixel, {
      layerFilter: (layer: any) =>  layer === this.mapLayer
    });

    const features = clusters.reduce((previous, current) =>
      [...previous, ...current.get('features')], []);

    return features;
  }

  getClusterStyle(feature) {
    const features = feature.get('features');
    const size = features.length;

    let tagScale = 0;
    const texturePaths = {};
    features.forEach((feature: TagFeature) => {
      const texture = feature.texture;
      const texturePath = getTagTexturePath(texture);
      tagScale = Math.max(tagScale, feature.size);

      if (!(texturePath in texturePaths)) {
        texturePaths[texturePath] = 0;
      }
      texturePaths[texturePath] += 1;
    });

    const mostFrequent = Object.keys(texturePaths)
      .sort((a,b) => texturePaths[a] - texturePaths[b])
      .reverse()
      .slice(0, 3);

    // Unique attributes that determines if we need a new style
    const attributes = [size, ...mostFrequent, tagScale];
    const key = attributes.join("-");

    let style = this.styleCache[key];

    if (!style) {
      const strokeColor = '#000000';
      const textColor = '#FFFFFF';
      const combinedStyles = [];
      const iconSize = tagIconSize *tagScale;

      // First texture in feature cluster
      combinedStyles.push(new Style({
        image: new Icon({
          src: mostFrequent[0],
          width: iconSize,
          height: iconSize,
          anchor: [0.5, 1],
          anchorXUnits: 'fraction',
          anchorYUnits: 'fraction'
        })
      }));

      // Second texture in feature cluster
      if (mostFrequent[1]) {
        combinedStyles.push(new Style({
          image: new Icon({
            src: mostFrequent[1],
            width: iconSize,
            height: iconSize,
            anchor: [0.5 + 0.2, 1],
            anchorXUnits: 'fraction',
            anchorYUnits: 'fraction'
          })
        }));
      }

      // Third texture in feature cluster
      if (mostFrequent[2]) {
        combinedStyles.push(new Style({
          image: new Icon({
            src: mostFrequent[2],
            width: iconSize,
            height: iconSize,
            anchor: [0.5 + 0.4, 1],
            anchorXUnits: 'fraction',
            anchorYUnits: 'fraction'
          })
        }));
      }

      combinedStyles.reverse();

      combinedStyles.push(new Style({
        text: new Text({
          text: size > 1 ? size.toString() : "",
          offsetY: -15,
          offsetX: 7.5,
          scale: 1.25,
          stroke: new Stroke({
            color: strokeColor,
            width: 3
          }),
          fill: new Fill({
            color: textColor
          }),
        }),
      }));

      style = [...combinedStyles];
      this.styleCache[key] = style;
    }

    return style;
  }

  reconcile(files: TagFile[]) {
    const features = [];

    files.forEach(file => {
      file.tags.forEach(tag => {
        if (!tag.visible) return;

        const point = new Point(tag.position.aerial);
        const feature = new TagFeature(
          tag.id, tag.texture,
          tag.size, point
        );

        features.push(feature);
      });
    });

    this.mapSource.clear();
    this.mapSource.addFeatures(features);
  }
}